import * as Sentry from "@sentry/vue";
import {
  computed,
  inject,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type Ref,
} from "vue";
import { cloneValidationValue } from "../helper/cloneValidationValue";
import { isValidationValueEqual } from "../helper/isValidationValueEqual";
import { runValidationRules } from "../helper/runValidationRules";
import { required as requiredRule } from "../rules/required";
import {
  FormValidationContextKey,
  type EqualityComparator,
  type FieldValidationController,
  type ValidationIssue,
  type ValidationRule,
  type ValidationStatus,
} from "../types/validation";

export interface UseFieldValidationOptions<TValue> {
  readonly value: Readonly<Ref<TValue>>;
  readonly name: MaybeRefOrGetter<string>;
  readonly displayName: MaybeRefOrGetter<string>;
  readonly rules: MaybeRefOrGetter<readonly ValidationRule<TValue>[]>;
  readonly required: MaybeRefOrGetter<boolean>;
  readonly disabled: MaybeRefOrGetter<boolean>;
  readonly trackDirty: MaybeRefOrGetter<boolean>;
  readonly comparator?: MaybeRefOrGetter<EqualityComparator<TValue> | undefined>;
  readonly updateValue: (value: TValue) => void;
}

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
}

export function useFieldValidation<TValue>(
  options: UseFieldValidationOptions<TValue>,
): FieldValidationController<TValue> {
  const formContext = inject(FormValidationContextKey, undefined);
  const id = Symbol("ValidationField");
  const baseline = ref<TValue>();
  const baselineReady = ref(false);
  const touched = ref(false);
  const status = ref<ValidationStatus>("pending-baseline");
  const issue = ref<ValidationIssue>();
  const runId = ref(0);
  let abortController: AbortController | undefined;
  let unregister: (() => void) | undefined;
  let stopWaitingForBaseline: (() => void) | undefined;
  let previousRuleSignature: unknown;

  const rules = computed(() => toValue(options.rules));
  const required = computed(() => toValue(options.required));
  const disabled = computed(() => toValue(options.disabled) || formContext?.disabled === true);
  const trackDirty = computed(() => toValue(options.trackDirty));
  const comparator = computed(() => toValue(options.comparator));
  const hasRules = computed(() => required.value || rules.value.length > 0);
  const pending = computed(() => status.value === "pending-validation");
  const baselinePending = computed(() => status.value === "pending-baseline");
  const valid = computed<boolean | undefined>(() => {
    if (status.value === "valid") {
      return true;
    }
    if (status.value === "invalid") {
      return false;
    }
    return undefined;
  });
  const dirty = computed(() => {
    if (!trackDirty.value || !baselineReady.value) {
      return false;
    }
    const equals = comparator.value ?? isValidationValueEqual;
    return !equals(options.value.value, baseline.value as TValue);
  });

  function abortValidation() {
    runId.value += 1;
    abortController?.abort();
    abortController = undefined;
  }

  async function validate(bypassDebounce = true): Promise<boolean> {
    abortValidation();

    if (!baselineReady.value) {
      status.value = "pending-baseline";
      issue.value = undefined;
      return false;
    }
    if (disabled.value) {
      status.value = "disabled";
      issue.value = undefined;
      return true;
    }
    if (!hasRules.value) {
      status.value = "unvalidated";
      issue.value = undefined;
      return true;
    }

    const currentRunId = runId.value;
    const currentAbortController = new AbortController();
    abortController = currentAbortController;
    status.value = "pending-validation";
    issue.value = undefined;

    const validationRules: readonly ValidationRule<TValue>[] = required.value
      ? [requiredRule<TValue>(), ...rules.value]
      : rules.value;

    try {
      const nextIssue = await runValidationRules(
        options.value.value,
        validationRules,
        {
          signal: currentAbortController.signal,
          fieldName: toValue(options.name),
          displayName: toValue(options.displayName),
        },
        bypassDebounce,
      );

      if (currentRunId !== runId.value || currentAbortController.signal.aborted) {
        return false;
      }

      issue.value = nextIssue;
      status.value = nextIssue == null ? "valid" : "invalid";
      return nextIssue == null;
    } catch (error) {
      if (currentRunId !== runId.value || isAbortError(error)) {
        return false;
      }

      Sentry.captureException(error, {
        tags: {
          validationRule: validationRules.find((rule) => rule.name)?.name ?? "unknown",
          validationField: toValue(options.name),
        },
      });
      issue.value = { messageKey: "validation.unexpected" };
      status.value = "invalid";
      return false;
    }
  }

  async function commitBaseline(): Promise<void> {
    await nextTick();
    baseline.value = cloneValidationValue(options.value.value);
    baselineReady.value = true;
    touched.value = false;
    await validate(false);
  }

  async function resetToBaseline(): Promise<void> {
    if (!baselineReady.value) {
      return;
    }

    const requestedValue = cloneValidationValue(baseline.value as TValue);
    options.updateValue(requestedValue);
    await nextTick();
    await nextTick();

    if (import.meta.env.DEV && !isValidationValueEqual(options.value.value, requestedValue)) {
      globalThis.console.warn(
        `InputRow "${toValue(options.name)}" normalized or rejected its baseline during reset`,
      );
    }

    baseline.value = cloneValidationValue(options.value.value);
    baselineReady.value = true;
    touched.value = false;
    await validate(false);
  }

  const controller = reactive({
    id,
    get name() {
      return toValue(options.name);
    },
    get displayName() {
      return toValue(options.displayName);
    },
    get value() {
      return options.value.value;
    },
    get baseline() {
      return baseline.value;
    },
    get status() {
      return status.value;
    },
    get issue() {
      return issue.value;
    },
    get valid() {
      return valid.value;
    },
    get invalid() {
      return status.value === "invalid";
    },
    get dirty() {
      return dirty.value;
    },
    get touched() {
      return touched.value;
    },
    get pending() {
      return pending.value;
    },
    get baselinePending() {
      return baselinePending.value;
    },
    get disabled() {
      return disabled.value;
    },
    get hasRules() {
      return hasRules.value;
    },
    get participatesInValidation() {
      return !disabled.value;
    },
    get contributesDirty() {
      return trackDirty.value;
    },
    get issues() {
      if (status.value === "invalid") {
        return [
          {
            id,
            name: toValue(options.name),
            displayName: toValue(options.displayName),
            path: [],
            status: "invalid" as const,
            issue: issue.value,
          },
        ];
      }
      if (status.value === "pending-validation") {
        return [
          {
            id,
            name: toValue(options.name),
            displayName: toValue(options.displayName),
            path: [],
            status: "pending-validation" as const,
          },
        ];
      }
      return [];
    },
    validate: () => validate(true),
    commitBaseline,
    resetToBaseline,
    async beginBaselineSession() {
      abortValidation();
      baselineReady.value = false;
      touched.value = false;
      issue.value = undefined;
      status.value = "pending-baseline";
    },
    destroy,
  }) as FieldValidationController<TValue>;

  function establishInitialBaseline() {
    if (formContext == null || !formContext.baselinePending) {
      void commitBaseline();
      return;
    }

    status.value = "pending-baseline";
    stopWaitingForBaseline = watch(
      () => formContext.baselinePending,
      (isPending) => {
        if (!isPending) {
          stopWaitingForBaseline?.();
          stopWaitingForBaseline = undefined;
          void commitBaseline();
        }
      },
    );
  }

  function destroy() {
    abortValidation();
    unregister?.();
    unregister = undefined;
    stopWaitingForBaseline?.();
    stopWaitingForBaseline = undefined;
  }

  watch(
    options.value,
    () => {
      if (baselineReady.value) {
        touched.value = true;
        void validate(false);
      }
    },
    { deep: true },
  );

  watch(
    () => ({
      required: required.value,
      rules: rules.value.map((rule) => ({
        name: rule.name,
        kind: rule.kind,
        dependencies: rule.dependencies,
        debounceMs: rule.kind === "async" ? rule.debounceMs : undefined,
      })),
    }),
    (signature) => {
      if (
        previousRuleSignature !== undefined &&
        isValidationValueEqual(signature, previousRuleSignature)
      ) {
        return;
      }
      previousRuleSignature = cloneValidationValue(signature);
      if (baselineReady.value) {
        void validate(false);
      }
    },
    { deep: true, immediate: true },
  );

  watch(disabled, (isDisabled) => {
    abortValidation();
    issue.value = undefined;
    if (isDisabled) {
      status.value = "disabled";
    } else if (baselineReady.value) {
      void validate(false);
    }
  });

  onMounted(() => {
    unregister = formContext?.register(controller);
    establishInitialBaseline();
  });
  onUnmounted(destroy);

  return controller;
}
